<template>
  <div>
    <BusinessEntityInput
      v-model="companyName"
      :error="errorCompanyName"
      :label="$t('Registered company name')"
      name="registered_company_name"
      :placeholder="$t('Full, registered company name')"
      required
      @focusout="v$.companyName.$touch"
    />

    <div class="tw-mt-4">
      <BusinessEntityInput
        v-model="companyNo"
        :error="errorCompanyNo"
        :label="$t('KvK number (Chamber of Commerce registration)')"
        name="company_no"
        placeholder="12345678"
        required
        @focusout="v$.companyNo.$touch"
      />
      <WdsTag v-if="kvkValidationStatus === 'valid'" success class="tw-mt-1">
        {{ $t('KvK valid') }}
      </WdsTag>
      <WdsTag v-else-if="kvkValidationStatus === 'invalid'" error class="tw-mt-1">
        {{ $t('KvK invalid') }}
      </WdsTag>
    </div>

    <div class="tw-mt-5">
      <BusinessEntityIsVatRegistered v-model="isVatSubject" market="NL" name="is_vat_subject" />
    </div>

    <div v-if="isVatSubject" class="tw-mt-4">
      <BusinessEntityInput
        v-model="vatNo"
        :error="errorVatNo"
        :label="$t('VAT number')"
        name="vat_no"
        placeholder="NL123456789B01"
        required
        @focusout="v$.vatNo.$touch"
      />
      <WdsTag v-if="taxValidationStatus === 'valid'" success class="tw-mt-1">
        {{ $t('VAT valid') }}
      </WdsTag>
      <WdsTag v-else-if="taxValidationStatus === 'invalid'" error class="tw-mt-1">
        {{ $t('VAT invalid') }}
      </WdsTag>
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { computed, ref } from 'vue'
import { WdsTag } from '@worksome/wds-components'
import BusinessEntityInput from '@/components/compliances/BusinessEntity/BusinessEntityInput.vue'
import BusinessEntityIsVatRegistered from '@/components/compliances/BusinessEntity/BusinessEntityIsVatRegistered.vue'
import {
  commonCompanyName,
  commonCompanyNumber,
} from '@/components/compliances/BusinessEntity/Validations/BusinessEntityValidationRules'
import { useValidatorWithGraphQlError } from '@/composables/errors'
import { setdefault } from '@/utils/tools'

export default {
  components: {
    BusinessEntityInput,
    BusinessEntityIsVatRegistered,
    WdsTag,
  },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    errorsGraphql: {
      type: Object,
      default() {
        return null
      },
    },
  },
  emits: ['update:businessEntity'],
  setup(props, { emit }) {
    const v$ = useVuelidate()
    const errors = ref(props.errorsGraphql)

    const internalBusinessEntity = ref(props.businessEntity)

    const companyName = computed({
      get() {
        return internalBusinessEntity.value.companyName
      },
      set(value) {
        internalBusinessEntity.value.companyName = value
        v$.value.companyName.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyName = useValidatorWithGraphQlError(
      v$,
      errors,
      'companyName',
      'registered_company_name',
    )

    const companyNo = computed({
      get() {
        return internalBusinessEntity.value.companyNo
      },
      set(value) {
        internalBusinessEntity.value.companyNo = value
        v$.value.companyNo.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyNo = useValidatorWithGraphQlError(v$, errors, 'companyNo', 'company_no')

    const isVatSubject = computed({
      get() {
        return internalBusinessEntity.value.data?.is_vat_subject ?? false
      },
      set(value) {
        const data = setdefault(internalBusinessEntity.value, 'data', {})
        data.is_vat_subject = value
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })

    const vatNo = computed({
      get() {
        return internalBusinessEntity.value.taxNo
      },
      set(value) {
        internalBusinessEntity.value.taxNo = value
        v$.value.vatNo.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorVatNo = useValidatorWithGraphQlError(v$, errors, 'vatNo', 'vat_no')

    const kvkValidationStatus = computed(() => internalBusinessEntity.value.data?.kvk_validation_status ?? null)
    const taxValidationStatus = computed(() => internalBusinessEntity.value.data?.tax_validation_status ?? null)

    return {
      companyName,
      companyNo,
      isVatSubject,
      vatNo,
      errorCompanyName,
      errorCompanyNo,
      errorVatNo,
      kvkValidationStatus,
      taxValidationStatus,
      internalBusinessEntity,
      v$,
    }
  },

  validations() {
    return {
      companyName: commonCompanyName(),
      companyNo: commonCompanyNumber(),
      vatNo: {},
    }
  },
}
</script>

